import {
  assertValidStudioDocument,
  normalizeStudioDocument,
} from "../StudioDocumentEngine";

export const STUDIO_SERIALIZATION_FORMAT_VERSION = 1;

export class StudioSerializationError extends Error {
  constructor(
    message,
    {
      code = "STUDIO_SERIALIZATION_ERROR",
      cause = null,
      details = {},
    } = {}
  ) {
    super(message);
    this.name = "StudioSerializationError";
    this.code = code;
    this.cause = cause;
    this.details =
      details &&
      typeof details === "object" &&
      !Array.isArray(details)
        ? { ...details }
        : {};
  }
}

export function canonicalizeStudioValue(
  value,
  path = "$",
  seen = new WeakSet()
) {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new StudioSerializationError(
        `${path} contains a non-finite number.`,
        {
          code: "NON_SERIALIZABLE_VALUE",
          details: { path },
        }
      );
    }

    return value;
  }

  if (
    typeof value === "undefined" ||
    typeof value === "function" ||
    typeof value === "symbol" ||
    typeof value === "bigint"
  ) {
    throw new StudioSerializationError(
      `${path} contains a non-serializable value.`,
      {
        code: "NON_SERIALIZABLE_VALUE",
        details: { path },
      }
    );
  }

  if (typeof value !== "object") {
    throw new StudioSerializationError(
      `${path} contains an unsupported value.`,
      {
        code: "NON_SERIALIZABLE_VALUE",
        details: { path },
      }
    );
  }

  if (seen.has(value)) {
    throw new StudioSerializationError(
      `${path} contains a circular reference.`,
      {
        code: "CIRCULAR_REFERENCE",
        details: { path },
      }
    );
  }

  seen.add(value);

  try {
    if (Array.isArray(value)) {
      return value.map((item, index) =>
        canonicalizeStudioValue(
          item,
          `${path}[${index}]`,
          seen
        )
      );
    }

    if (!isPlainObject(value)) {
      throw new StudioSerializationError(
        `${path} contains a non-plain object.`,
        {
          code: "NON_SERIALIZABLE_VALUE",
          details: { path },
        }
      );
    }

    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] = canonicalizeStudioValue(
          value[key],
          `${path}.${key}`,
          seen
        );
        return result;
      }, {});
  } finally {
    seen.delete(value);
  }
}

export function canonicalizeStudioDocument(document) {
  assertDocument(document);

  try {
    return canonicalizeStudioValue(document);
  } catch (error) {
    throw wrapSerializationError(
      error,
      "Studio document canonicalization failed.",
      "CANONICALIZATION_FAILED"
    );
  }
}

export function serializeStudioDocument(
  document,
  {
    formatVersion = STUDIO_SERIALIZATION_FORMAT_VERSION,
  } = {}
) {
  validateFormatVersion(formatVersion);
  const canonicalDocument =
    canonicalizeStudioDocument(document);

  return canonicalizeStudioValue({
    formatVersion,
    document: canonicalDocument,
  });
}

export function stringifyStudioDocument(
  document,
  {
    formatVersion = STUDIO_SERIALIZATION_FORMAT_VERSION,
    space = 0,
  } = {}
) {
  const envelope = serializeStudioDocument(document, {
    formatVersion,
  });

  try {
    return JSON.stringify(
      envelope,
      null,
      normalizeJsonSpace(space)
    );
  } catch (error) {
    throw wrapSerializationError(
      error,
      "Studio document stringification failed.",
      "STRINGIFY_FAILED"
    );
  }
}

export function deserializeStudioDocument(
  serialized,
  {
    supportedFormatVersions = [
      STUDIO_SERIALIZATION_FORMAT_VERSION,
    ],
    normalize = false,
  } = {}
) {
  if (!isPlainObject(serialized)) {
    throw new StudioSerializationError(
      "Serialized Studio payload must be a plain object.",
      {
        code: "INVALID_SERIALIZED_PAYLOAD",
      }
    );
  }

  validateSupportedVersions(supportedFormatVersions);

  const formatVersion = serialized.formatVersion;

  validateFormatVersion(formatVersion);

  if (!supportedFormatVersions.includes(formatVersion)) {
    throw new StudioSerializationError(
      `Unsupported Studio serialization format version: ${formatVersion}.`,
      {
        code: "UNSUPPORTED_FORMAT_VERSION",
        details: {
          formatVersion,
          supportedFormatVersions: [
            ...supportedFormatVersions,
          ],
        },
      }
    );
  }

  if (!Object.prototype.hasOwnProperty.call(
    serialized,
    "document"
  )) {
    throw new StudioSerializationError(
      "Serialized Studio payload is missing document.",
      {
        code: "MISSING_DOCUMENT",
      }
    );
  }

  const document = normalize
    ? normalizeStudioDocument(serialized.document)
    : serialized.document;

  assertDocument(document);

  return canonicalizeStudioDocument(document);
}

export function parseStudioDocument(
  serializedText,
  options = {}
) {
  if (typeof serializedText !== "string") {
    throw new StudioSerializationError(
      "Serialized Studio text must be a string.",
      {
        code: "INVALID_SERIALIZED_TEXT",
      }
    );
  }

  let parsed;

  try {
    parsed = JSON.parse(serializedText);
  } catch (error) {
    throw new StudioSerializationError(
      "Studio document JSON parsing failed.",
      {
        code: "PARSE_FAILED",
        cause: error,
      }
    );
  }

  return deserializeStudioDocument(parsed, options);
}

function assertDocument(document) {
  try {
    assertValidStudioDocument(document);
  } catch (error) {
    throw new StudioSerializationError(
      "Studio document validation failed before serialization.",
      {
        code: "INVALID_STUDIO_DOCUMENT",
        cause: error,
        details: {
          issues: Array.isArray(error?.issues)
            ? [...error.issues]
            : [],
        },
      }
    );
  }
}

function validateFormatVersion(formatVersion) {
  if (
    !Number.isInteger(formatVersion) ||
    formatVersion < 1
  ) {
    throw new StudioSerializationError(
      "Serialization formatVersion must be a positive integer.",
      {
        code: "INVALID_FORMAT_VERSION",
        details: { formatVersion },
      }
    );
  }
}

function validateSupportedVersions(versions) {
  if (
    !Array.isArray(versions) ||
    versions.length === 0 ||
    versions.some(
      (version) =>
        !Number.isInteger(version) || version < 1
    )
  ) {
    throw new StudioSerializationError(
      "supportedFormatVersions must contain positive integers.",
      {
        code: "INVALID_SUPPORTED_FORMAT_VERSIONS",
      }
    );
  }
}

function normalizeJsonSpace(space) {
  if (typeof space === "number") {
    return Math.max(0, Math.min(10, Math.trunc(space)));
  }

  if (typeof space === "string") {
    return space.slice(0, 10);
  }

  return 0;
}

function wrapSerializationError(
  error,
  message,
  fallbackCode
) {
  if (error instanceof StudioSerializationError) {
    return error;
  }

  return new StudioSerializationError(message, {
    code: fallbackCode,
    cause: error,
  });
}

function isPlainObject(value) {
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return (
    prototype === Object.prototype ||
    prototype === null
  );
}
